import React, { useState, useEffect } from 'react';
import '../../styles/AdminPages.css';
import './GestionOpportunites.css';
import DashboardHeader from '../../components/DashboardHeader';
import AdminNavigationBar from '../../components/AdminNavigationBar';
import opportuniteService from '../../services/opportuniteService';

function GestionOpportunites() {
  const [opportunites, setOpportunites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [formData, setFormData] = useState({
    titre: '',
    type: 'stage',
    description: '',
    contenu: '',
    image: null,
    organisme: '',
    pays: '',
    ville: '',
    date_limite: '',
    lien_candidature: '',
    criteres: [''],
    documents_requis: [''],
    publie: true
  });

  useEffect(() => {
    loadOpportunites();
  }, [filterType]);

  const loadOpportunites = async () => {
    try {
      setLoading(true);
      const params = filterType !== 'all' ? { type: filterType } : {};
      try {
        const response = await opportuniteService.admin.getAll(params);
        setOpportunites(response.data.data || response.data || []);
      } catch (adminError) {
        console.warn('Fallback vers opportunités publiques:', adminError);
        const response = await opportuniteService.getAll(params);
        setOpportunites(response.data.data || response.data || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des opportunités:', error);
      setOpportunites([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      
      // Ajouter tous les champs sauf les arrays et l'image
      Object.keys(formData).forEach(key => {
        if (key === 'image') {
          // Ajouter l'image SEULEMENT si elle existe et est un fichier
          if (formData[key] && formData[key] instanceof File) {
            formDataToSend.append(key, formData[key]);
          }
        } else if (key === 'criteres') {
          const criteres = formData.criteres.filter(c => c.trim() !== '');
          formDataToSend.append(key, JSON.stringify(criteres));
        } else if (key === 'documents_requis') {
          const docs = formData.documents_requis.filter(d => d.trim() !== '');
          formDataToSend.append(key, JSON.stringify(docs));
        } else if (key === 'publie') {
          // Convertir le booléen en string
          formDataToSend.append(key, formData[key] ? '1' : '0');
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (editingId) {
        // Ajouter _method pour Laravel
        formDataToSend.append('_method', 'PUT');
        await opportuniteService.admin.update(editingId, formDataToSend);
        alert('Opportunité mise à jour avec succès');
      } else {
        await opportuniteService.admin.create(formDataToSend);
        alert('Opportunité créée avec succès');
      }
      
      setShowModal(false);
      resetForm();
      loadOpportunites();
    } catch (error) {
      console.error('Erreur complète:', error);
      console.error('Réponse:', error.response?.data);
      alert(`Erreur lors de l'enregistrement: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await opportuniteService.admin.getById(id);
      const opp = response.data;
      setFormData({
        titre: opp.titre,
        type: opp.type,
        description: opp.description,
        contenu: opp.contenu,
        image: null,
        organisme: opp.organisme,
        pays: opp.pays,
        ville: opp.ville || '',
        date_limite: opp.date_limite,
        lien_candidature: opp.lien_candidature || '',
        criteres: opp.criteres || [''],
        documents_requis: opp.documents_requis || [''],
        publie: opp.publie
      });
      setEditingId(id);
      setShowModal(true);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du chargement de l\'opportunité');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette opportunité ?')) {
      return;
    }
    try {
      await opportuniteService.admin.delete(id);
      alert('Opportunité supprimée avec succès');
      loadOpportunites();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      titre: '',
      type: 'stage',
      description: '',
      contenu: '',
      image: null,
      organisme: '',
      pays: '',
      ville: '',
      date_limite: '',
      lien_candidature: '',
      criteres: [''],
      documents_requis: [''],
      publie: true
    });
    setEditingId(null);
  };

  const handleArrayFieldChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const removeArrayField = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <AdminNavigationBar 
        title="Gestion des Opportunités" 
        onBack={() => window.navigateTo && window.navigateTo('admin-dashboard')}
      />
      <div className="admin-container">
        <main className="admin-content admin-content-full">
          <div className="page-header">
            <button className="btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
              + Nouvelle Opportunité
            </button>
          </div>

          <div className="filters">
            <button 
              className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
              onClick={() => setFilterType('all')}
            >
              Toutes
            </button>
            <button 
              className={`filter-btn ${filterType === 'stage' ? 'active' : ''}`}
              onClick={() => setFilterType('stage')}
            >
              Stages
            </button>
            <button 
              className={`filter-btn ${filterType === 'bourse' ? 'active' : ''}`}
              onClick={() => setFilterType('bourse')}
            >
              Bourses
            </button>
          </div>

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <div className="opportunites-table">
              <table>
                <thead>
                  <tr>
                    <th>Titre</th>
                    <th>Type</th>
                    <th>Organisme</th>
                    <th>Pays</th>
                    <th>Date limite</th>
                    <th>Statut</th>
                    <th>Vues</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {opportunites.map((opp) => (
                    <tr key={opp.id}>
                      <td>{opp.titre}</td>
                      <td>
                        <span className={`badge badge-${opp.type}`}>
                          {opp.type === 'stage' ? 'Stage' : 'Bourse'}
                        </span>
                      </td>
                      <td>{opp.organisme}</td>
                      <td>{opp.pays}</td>
                      <td>{new Date(opp.date_limite).toLocaleDateString('fr-FR')}</td>
                      <td>
                        <span className={`badge ${opp.publie ? 'badge-success' : 'badge-draft'}`}>
                          {opp.publie ? 'Publié' : 'Brouillon'}
                        </span>
                      </td>
                      <td>{opp.vues}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-edit" onClick={() => handleEdit(opp.id)}>
                            Modifier
                          </button>
                          <button className="btn-delete" onClick={() => handleDelete(opp.id)}>
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {showModal && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editingId ? 'Modifier l\'opportunité' : 'Nouvelle opportunité'}</h2>
                  <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
                </div>
                <form onSubmit={handleSubmit} className="opportunite-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Titre *</label>
                      <input
                        type="text"
                        value={formData.titre}
                        onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Type *</label>
                      <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        required
                      >
                        <option value="stage">Stage</option>
                        <option value="bourse">Bourse</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Description courte *</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="3"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Contenu détaillé *</label>
                    <textarea
                      value={formData.contenu}
                      onChange={(e) => setFormData({ ...formData, contenu: e.target.value })}
                      rows="5"
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Organisme *</label>
                      <input
                        type="text"
                        value={formData.organisme}
                        onChange={(e) => setFormData({ ...formData, organisme: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Image</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Pays *</label>
                      <input
                        type="text"
                        value={formData.pays}
                        onChange={(e) => setFormData({ ...formData, pays: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Ville</label>
                      <input
                        type="text"
                        value={formData.ville}
                        onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Date limite *</label>
                      <input
                        type="date"
                        value={formData.date_limite}
                        onChange={(e) => setFormData({ ...formData, date_limite: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Lien de candidature</label>
                      <input
                        type="url"
                        value={formData.lien_candidature}
                        onChange={(e) => setFormData({ ...formData, lien_candidature: e.target.value })}
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Critères d'éligibilité</label>
                    {formData.criteres.map((critere, index) => (
                      <div key={index} className="array-field">
                        <input
                          type="text"
                          value={critere}
                          onChange={(e) => handleArrayFieldChange('criteres', index, e.target.value)}
                          placeholder="Critère..."
                        />
                        {formData.criteres.length > 1 && (
                          <button type="button" onClick={() => removeArrayField('criteres', index)}>×</button>
                        )}
                      </div>
                    ))}
                    <button type="button" className="btn-add-field" onClick={() => addArrayField('criteres')}>
                      + Ajouter un critère
                    </button>
                  </div>

                  <div className="form-group">
                    <label>Documents requis</label>
                    {formData.documents_requis.map((doc, index) => (
                      <div key={index} className="array-field">
                        <input
                          type="text"
                          value={doc}
                          onChange={(e) => handleArrayFieldChange('documents_requis', index, e.target.value)}
                          placeholder="Document..."
                        />
                        {formData.documents_requis.length > 1 && (
                          <button type="button" onClick={() => removeArrayField('documents_requis', index)}>×</button>
                        )}
                      </div>
                    ))}
                    <button type="button" className="btn-add-field" onClick={() => addArrayField('documents_requis')}>
                      + Ajouter un document
                    </button>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.publie}
                        onChange={(e) => setFormData({ ...formData, publie: e.target.checked })}
                      />
                      Publier immédiatement
                    </label>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                      Annuler
                    </button>
                    <button type="submit" className="btn-submit">
                      {editingId ? 'Mettre à jour' : 'Créer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default GestionOpportunites;